import { Base } from '@studiometa/js-toolkit';
import { toggleClass, addClass } from '@studiometa/js-toolkit/utils';
import Menu from './Menu/index.js';
import { gsap } from 'gsap';
import CustomEase from 'gsap/CustomEase.js';
import CSSPlugin from 'gsap/CSSPlugin.js';

gsap.registerPlugin(CSSPlugin);

/**
 * Toast component
 */
export default class Header extends Base {
  currentMenu = null;
  menuIsOpen = false;
  navIsOpen = false;

  /**
   * Component Configuration
   */
  static config = {
    name: 'Header',
    refs: ['overlay[]', 'close', 'closeMobile[]', 'burger', 'nav', 'navItems[]'],
    components: {
      Menu,
    },
  };

  /**
   * Create Timeline to animat global elements before show menu
   */
  mounted() {
    this.initMenuTimeline();
    if (this.isMobile) {
      this.initNavTimeline();
    }

    if (scrollY > 120) {
      addClass(document.documentElement, 'is-scrolling-up');
    }
  }

  /**
   * Test if we are on the mobile version of the component or not.
   * @returns {boolean}
   */
  get isMobile() {
    return ['xxs', 'xs', 's', 'm'].includes(this.$services.get('resized').breakpoint);
  }

  /**
   *
   */
  onBurgerClick() {
    if (!this.navIsOpen) {
      document.body.classList.add('menu-open');
      this.navTimeline.play();
      this.navIsOpen = true;
    } else {
      this.navTimeline.reverse();
      setTimeout(() => {
        document.body.classList.remove('menu-open');
      }, 500);
      this.navIsOpen = false;
    }
  }

  /**
   * Play timeline of globals elements of Menu
   */
  openMenuElements() {
    if (this.isMobile) {
      this.currentMenu.openMenu();
    } else {
      document.body.classList.add('overflow-hidden');
      this.menuTimeline.restart();
    }
  }

  /**
   * Reverse timeline of globals elements of Menu to close it
   */
  closeMenuElements() {
    document.body.classList.remove('overflow-hidden');
    this.menuTimeline.reverse(0, false);
  }

  /**
   * Click on close Button
   * @param {object} props Props
   * @param {object} props.event current event
   * @returns {void}
   */
  onCloseMobileClick({ event }) {
    this.currentMenu.closeMenu();
    this.currentMenu = null;
    event.stopPropagation();
  }

  /**
   * Click on close Button
   */
  onCloseClick() {
    this.currentMenu.closeMenu();
    this.currentMenu = null;
  }

  /**
   * When clicking on main nav, switch or open menu
   * @param {object} props Props
   * @param {object} props.target Current element
   * @returns {void}
   */
  onMenuClickItem({ target }) {
    if (this.currentMenu === null) {
      this.currentMenu = target;
      this.openMenuElements();
      return;
    }
    if (this.currentMenu !== target) {
      this.currentMenu.switchMenu();
      this.currentMenu = target;
    }
  }

  /**
   * When switching animation is finished open new menu !
   */
  onMenuSwitchComplete() {
    this.currentMenu.openMenu();
  }

  /**
   * Service scrolled hook
   * @see https://js-toolkit.studiometa.dev/api/methods-hooks-services.html#scrolled
   * @param {object} props Props
   * @param {number} props.y The current vertical scroll position.
   * @param {object} props.direction For both axis, whether or not the current position has changed since the last one.
   */
  scrolled({ y, direction }) {
    const isOnTop = y <= 0;
    const isNearTop = y < 120;

    toggleClass(document.documentElement, 'is-on-top', isOnTop);
    toggleClass(document.documentElement, 'is-near-top', isNearTop);

    if (direction.y === 'NONE') {
      return;
    }

    toggleClass(document.documentElement, 'is-scrolling-down', direction.y === 'DOWN');
    toggleClass(document.documentElement, 'is-scrolling-up', direction.y === 'UP');
  }

  /**
   * Init animations for menu
   */
  initMenuTimeline() {
    this.menuTimeline = gsap.timeline({
      onUpdate: () => {
        const progress = this.menuTimeline.progress();
        if (this.menuIsOpen === false) {
          if (progress > 0.3 && progress < 0.4) {
            document.body.classList.add('open-submenu');
            this.menuIsOpen = true;
          }
        } else {
          if (progress < 0.3) {
            document.body.classList.remove('open-submenu');
            this.menuIsOpen = false;
          }
        }
      },
    });

    this.menuTimeline.pause();
    this.menuTimeline.to(this.$refs.overlay, {
      scaleY: 1,
      ease: 'power3.inOut',
      duration: 0.6,
      stagger: {
        grid: [1, 6],
        amount: 0.3,
        ease: CustomEase.create('custom', '0.215, 0.61, 0.355, 1'),
      },
      onComplete: () => {
        this.currentMenu.openMenu();
      },
    });

    this.menuTimeline.to(this.$refs.close, {
      width: '56px',
      ease: CustomEase.create('custom', '0.215, 0.61, 0.355, 1'),
    });
  }

  /**
   * Init animations for nav timeline
   */
  initNavTimeline() {
    this.navTimeline = gsap.timeline();
    this.navTimeline.pause();
    this.navTimeline.to(this.$refs.nav, {
      x: 0,
      duration: 0.4,
      ease: CustomEase.create('custom', '0.215, 0.61, 0.355, 1'),
    });
    this.navTimeline.fromTo(
      this.$refs.navItems,
      {
        x: 50,
        opacity: 0,
      },
      {
        x: 0,
        clearProps: 'transform',
        parseTransform: true,
        opacity: 1,
        stagger: 0.03,
        ease: CustomEase.create('custom', '0.215, 0.61, 0.355, 1'),
      },
      '-=0.2',
    );
  }
}
